import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { formatTimeLabel, hasValues, mergeSeries, timeAxisProps, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface THITimeSeriesChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
}

const THI_KEY = 'THI';

function getTHIColor(value: number): string {
  if (value >= 88) {
    return '#ef4444';
  }

  if (value >= 78) {
    return '#f97316';
  }

  if (value >= 72) {
    return '#f59e0b';
  }

  return '#22c55e';
}

function CustomDot({ cx, cy, payload }: any) {
  const value = payload?.[THI_KEY];

  if (value == null) {
    return null;
  }

  return <circle cx={cx} cy={cy} r={2} fill={getTHIColor(value)} />;
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  const value = payload[0]?.value;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{formatTimeLabel(label)}</p>
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: value != null ? getTHIColor(value) : '#9ca3af' }}
        />
        <span className="text-gray-600">THI:</span>
        <span className="font-medium text-gray-900">{value != null ? `${value} THI` : '—'}</span>
      </div>
    </div>
  );
}

const THI_FORMULA = 'THI = (1.8×T + 32) − (0.55 − 0.0055×RH) × (1.8×T − 26)';

const THITimeSeriesChart: React.FC<THITimeSeriesChartProps> = ({ title, series, range }) => {
  const { t } = useTranslation();
  const [showFormula, setShowFormula] = React.useState(false);

  const mergedData = React.useMemo(() => mergeSeries(series, range), [series, range]);
  const hasData = hasValues(series);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-xs text-gray-500">&lt; 72</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-amber-400" />
              <span className="text-xs text-gray-500">72–78</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-xs text-gray-500">78–88</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-gray-500">≥ 88</span>
            </div>
            <div className="relative">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onMouseEnter={() => setShowFormula(true)}
                onMouseLeave={() => setShowFormula(false)}
              >
                <Info className="h-4 w-4" />
              </button>
              {showFormula && (
                <div className="absolute right-0 top-6 z-10 w-72 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg text-xs">
                  <p className="font-semibold text-gray-700 mb-1">THI Formula</p>
                  <p className="text-gray-600 font-mono">{THI_FORMULA}</p>
                  <p className="text-gray-400 mt-1">T = inside temperature (°C), RH = inside humidity (%)</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState
            title={`${t('f-no-data-placeholder', { value: 'THI' })}`}
            description={`${t('f-sensor-not-available')}`}
          />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={mergedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...timeAxisProps(range)} />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={40}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine
                y={78}
                stroke="#f97316"
                strokeDasharray="5 5"
                label={{ value: 'Heat stress risk 78', position: 'right', fill: '#f97316', fontSize: 10 }}
              />
              <ReferenceLine
                y={72}
                stroke="#f59e0b"
                strokeDasharray="5 5"
                label={{ value: 'Heat stress risk 72', position: 'right', fill: '#f59e0b', fontSize: 10 }}
              />
              <Line
                type="monotone"
                dataKey={THI_KEY}
                stroke="#f59e0b"
                strokeWidth={2}
                dot={<CustomDot />}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default THITimeSeriesChart;
